/**
 * Playbook extraction — the staging rules and the live-progress state machine.
 *
 * Two things live here, both pure, because both used to live inside a
 * component and neither could be tested there:
 *
 *  1. **Staging.** A playbook is now generated from N documents, not one. The
 *     user drops files, sees what was taken and what was refused, removes what
 *     they didn't mean to include, and only then presses generate. Nothing
 *     leaves the browser until that press.
 *  2. **Progress.** The AI engine emits `PLAYBOOK_EXTRACTION_PROGRESS` on the
 *     `playbook_{uuid}` group with a step count it decides at runtime and a
 *     per-file sub-progress underneath the current step. The envelope shape
 *     varies by emitter (`data` nesting, snake vs camel), so every event is
 *     normalized once, here, and the reducer below is the only thing that
 *     decides what the screen says.
 */

// ─── Upload contract (mirrors the backend's accepted set) ────────────────────

/** Documents the extractor can read, plus the image formats it OCRs. */
export const PLAYBOOK_SOURCE_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'txt', 'md', 'csv', 'xls', 'xlsx',
  'png', 'jpg', 'jpeg', 'tif', 'tiff', 'bmp', 'webp',
];

export const PLAYBOOK_MAX_FILES = 20;
export const PLAYBOOK_MAX_FILE_BYTES = 50 * 1024 * 1024;

/** The PubSub group the AI engine publishes extraction progress to. */
export const playbookGroup = (playbookUuid: string) => `playbook_${playbookUuid}`;

/** The event name the AI engine emits progress under. */
export const PLAYBOOK_EXTRACTION_EVENT = 'PLAYBOOK_EXTRACTION_PROGRESS';

// ─── Step labels ─────────────────────────────────────────────────────────────

/**
 * Fallback copy for the pipeline's steps.
 *
 * The engine sends a readable `step_name` and we render that verbatim. This map
 * exists for the events that carry only a machine id (`extract_rules`) — before
 * it, the panel printed the id. Every label here describes what the PLAYBOOK
 * extractor does; the shared `ai_processing` pipeline's matter-flavoured copy
 * must never reach this screen.
 */
export const PLAYBOOK_STEP_LABELS: Record<string, string> = {
  download_documents: 'Downloading documents',
  downloading_documents: 'Downloading documents',
  download: 'Downloading documents',
  fetch_documents: 'Downloading documents',
  read_documents: 'Downloading documents',

  analyze_clauses: 'Analyzing clauses',
  analyzing_clauses: 'Analyzing clauses',
  analyze: 'Analyzing clauses',
  clause_analysis: 'Analyzing clauses',

  extract_rules: 'Extracting rules',
  extracting_rules: 'Extracting rules',
  extract: 'Extracting rules',
  rule_extraction: 'Extracting rules',

  merge_rules: 'Merging with existing rules',
  merging_rules: 'Merging with existing rules',
  merge: 'Merging with existing rules',
  deduplicate_rules: 'Merging with existing rules',
};

const stepKeyOf = (raw: string): string =>
  raw.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

/**
 * A step id turned into something a lawyer can read.
 *
 * A name the engine already wrote in prose ("Analyzing clause 4 of 12") passes
 * straight through — only bare ids are translated or title-cased.
 */
export function playbookStepLabel(raw: string): string {
  const key = stepKeyOf(raw);
  if (PLAYBOOK_STEP_LABELS[key]) return PLAYBOOK_STEP_LABELS[key];
  // Prose already: leave it exactly as the engine wrote it.
  if (/\s/.test(raw.trim())) return raw.trim();
  if (!key) return raw.trim();
  const words = key.split('_');
  return words[0].charAt(0).toUpperCase() + words[0].slice(1) + (words.length > 1 ? ` ${words.slice(1).join(' ')}` : '');
}

// ─── Staging ─────────────────────────────────────────────────────────────────

export interface StagedPlaybookFile {
  /** Stable across re-renders and unique within the batch. */
  id: string;
  name: string;
  size: number;
  file: File;
}

export type StageRejectionReason = 'duplicate' | 'limit';

export interface StageRejection {
  name: string;
  reason: StageRejectionReason;
}

export interface StageResult {
  staged: StagedPlaybookFile[];
  rejected: StageRejection[];
}

const fingerprint = (name: string, size: number) => `${name}:${size}`;

/**
 * Add `incoming` to what is already staged.
 *
 * Dropping the same folder twice is the common accident, so a file matching an
 * already-staged name AND size is refused rather than uploaded twice — the
 * backend would happily extract the same document into duplicate rules.
 */
export function stagePlaybookFiles(
  existing: StagedPlaybookFile[],
  incoming: File[],
  maxFiles: number = PLAYBOOK_MAX_FILES,
): StageResult {
  const seen = new Set(existing.map((s) => fingerprint(s.name, s.size)));
  const staged = [...existing];
  const rejected: StageRejection[] = [];

  incoming.forEach((file, index) => {
    const key = fingerprint(file.name, file.size);
    if (seen.has(key)) {
      rejected.push({ name: file.name, reason: 'duplicate' });
      return;
    }
    if (staged.length >= maxFiles) {
      rejected.push({ name: file.name, reason: 'limit' });
      return;
    }
    seen.add(key);
    staged.push({
      id: `${key}:${staged.length}:${index}`,
      name: file.name,
      size: file.size,
      file,
    });
  });

  return { staged, rejected };
}

export function removeStagedFile(
  staged: StagedPlaybookFile[],
  id: string,
): StagedPlaybookFile[] {
  return staged.filter((s) => s.id !== id);
}

// ─── Live progress state ─────────────────────────────────────────────────────

export type ExtractionRunStatus =
  | 'idle'
  | 'connecting'
  | 'running'
  | 'completed'
  | 'failed';

export type ExtractionStepStatus = 'pending' | 'in_progress' | 'completed' | 'failed';

export interface ExtractionRunStep {
  id: string;
  name: string;
  status: ExtractionStepStatus;
  message?: string;
  /** The engine's optional plain-language aside about this step. */
  narration?: string;
}

export type ExtractionFileStatus = 'pending' | 'in_progress' | 'completed' | 'failed';

export interface ExtractionFileProgress {
  name: string;
  /** 0–100, or null while the engine hasn't reported a number for this file. */
  percent: number | null;
  status: ExtractionFileStatus;
  message?: string;
}

export interface FailedDocument {
  name: string;
  reason?: string;
}

export interface ExtractionRun {
  status: ExtractionRunStatus;
  /** Steps in arrival order. The engine decides how many there are. */
  steps: ExtractionRunStep[];
  /** Reported step total when the engine sends one, else the count seen so far. */
  stepTotal: number;
  /**
   * Which step the engine says it is on, 1-based. Taken from `step_index` when
   * the engine sends one, else derived from how many steps have opened — so
   * "Step 3 of 7" tracks the pipeline instead of a guess.
   */
  stepCurrent: number;
  /** 0–100. Reported when the engine sends a number, else derived from steps. */
  percent: number;
  files: ExtractionFileProgress[];
  /** Files the extractor could not read. Populated by the completion event. */
  failedDocuments: FailedDocument[];
  /** Latest human-readable line from the engine. */
  message: string | null;
  /** The engine's optional secondary line explaining what it is doing. */
  narration: string | null;
  error: string | null;
}

export const initialExtractionRun: ExtractionRun = {
  status: 'idle',
  steps: [],
  stepTotal: 0,
  stepCurrent: 0,
  percent: 0,
  files: [],
  failedDocuments: [],
  message: null,
  narration: null,
  error: null,
};

/**
 * Seed a run from the files the user just submitted, so the per-file list is
 * on screen from the first frame instead of appearing one row at a time as
 * the engine happens to mention each name.
 */
export function beginExtractionRun(fileNames: string[]): ExtractionRun {
  return {
    ...initialExtractionRun,
    status: 'connecting',
    files: fileNames.map((name) => ({ name, percent: null, status: 'pending' })),
  };
}

// ─── Event normalization ─────────────────────────────────────────────────────

export interface NormalizedProgress {
  kind: 'progress';
  playbookUuid: string | null;
  stepId: string | null;
  stepName: string | null;
  stepStatus: ExtractionStepStatus;
  stepIndex: number | null;
  stepTotal: number | null;
  percent: number | null;
  message: string | null;
  /** Optional secondary line from the engine — always de-emphasized. */
  narration: string | null;
  files: ExtractionFileProgress[];
}

export interface NormalizedCompletion {
  kind: 'completed';
  playbookUuid: string | null;
  message: string | null;
  failedDocuments: FailedDocument[];
}

export interface NormalizedFailure {
  kind: 'failed';
  playbookUuid: string | null;
  error: string;
  failedDocuments: FailedDocument[];
}

export type PlaybookExtractionEvent =
  | NormalizedProgress
  | NormalizedCompletion
  | NormalizedFailure
  | { kind: 'ignore' };

const clampPercent = (value: any): number | null => {
  const n = typeof value === 'string' ? Number(value) : value;
  if (typeof n !== 'number' || !Number.isFinite(n)) return null;
  return Math.min(100, Math.max(0, Math.round(n)));
};

const str = (value: any): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

/**
 * Copy that belongs to another product surface, refused.
 *
 * `ai_processing` is a SHARED hub: matter analysis, drafting and research all
 * publish onto it, and the wildcard listener sees every one of them. The
 * pipeline's generic opener ("Preparing your matter documents…") was therefore
 * appearing verbatim under "Generating playbook…". A playbook has no matter, so
 * any line that mentions one is dropped rather than shown.
 */
const isForeignCopy = (value: string): boolean => /\bmatters?\b/i.test(value);

const playbookStr = (value: any): string | null => {
  const s = str(value);
  return s && !isForeignCopy(s) ? s : null;
};

/**
 * Ids belonging to another entity on this hub. Their presence — with no
 * matching `playbook_uuid` — means the event is somebody else's.
 */
const FOREIGN_ID_KEYS = [
  'matter_uuid', 'matterUuid', 'MatterUUID',
  'chat_uuid', 'chatUuid',
  'session_uuid', 'sessionUuid',
  'review_uuid', 'reviewUuid',
  'draft_uuid', 'draftUuid',
  'table_uuid', 'index_uuid',
];

/** Event-name prefixes owned by other features on the shared hub. */
const FOREIGN_EVENT_PREFIXES = ['research.', 'review.', 'matter.', 'chat.', 'notification'];

/**
 * `failed_documents` arrives either as bare filenames or as objects carrying a
 * reason. Both mean the same thing to the reader, so both land in one shape.
 */
export function normalizeFailedDocuments(raw: any): FailedDocument[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry): FailedDocument | null => {
      if (typeof entry === 'string') {
        return str(entry) ? { name: entry.trim() } : null;
      }
      if (!entry || typeof entry !== 'object') return null;
      const name =
        str(entry.original_filename) ||
        str(entry.file_name) ||
        str(entry.filename) ||
        str(entry.name);
      if (!name) return null;
      const reason = str(entry.reason) || str(entry.error) || str(entry.message);
      return reason ? { name, reason } : { name };
    })
    .filter((d): d is FailedDocument => d !== null);
}

const normalizeFileRows = (data: any): ExtractionFileProgress[] => {
  // Shape A: an explicit array of per-file rows.
  const list = data.files || data.documents || data.file_progress;
  if (Array.isArray(list)) {
    return list
      .map((entry: any): ExtractionFileProgress | null => {
        if (typeof entry === 'string') {
          return str(entry) ? { name: entry.trim(), percent: null, status: 'pending' } : null;
        }
        if (!entry || typeof entry !== 'object') return null;
        const name =
          str(entry.original_filename) ||
          str(entry.file_name) ||
          str(entry.filename) ||
          str(entry.name);
        if (!name) return null;
        const rawStatus = String(entry.status || '').toLowerCase();
        const status: ExtractionFileStatus =
          rawStatus === 'completed' || rawStatus === 'complete'
            ? 'completed'
            : rawStatus === 'failed' || rawStatus === 'error'
              ? 'failed'
              : rawStatus === 'pending'
                ? 'pending'
                : 'in_progress';
        return {
          name,
          percent: clampPercent(entry.percent ?? entry.progress_percent),
          status,
          message: str(entry.message) || undefined,
        };
      })
      .filter((f): f is ExtractionFileProgress => f !== null);
  }

  // Shape B: "the engine is on this one file right now".
  const current =
    str(data.current_file) ||
    str(data.file_name) ||
    str(data.filename) ||
    str(data.document_name);
  if (!current) return [];
  const percent = clampPercent(
    data.file_progress_percent ?? data.file_percent ?? data.document_percent,
  );
  return [{ name: current, percent, status: 'in_progress' }];
};

/**
 * Flatten one PubSub envelope into something the reducer can act on.
 *
 * Returns `{ kind: 'ignore' }` for connection chatter and for events belonging
 * to a different playbook — the wildcard listener sees the whole hub.
 */
export function normalizePlaybookEvent(
  raw: any,
  eventName?: string,
  playbookUuid?: string,
): PlaybookExtractionEvent {
  const envelope = Array.isArray(raw) ? raw[0] : raw;
  if (!envelope || typeof envelope !== 'object') return { kind: 'ignore' };

  const inner = envelope.data && typeof envelope.data === 'object' ? envelope.data : {};
  const data: any = { ...envelope, ...inner };

  const eventPlaybookUuid = str(data.playbook_uuid) || str(data.playbookUuid);
  if (eventPlaybookUuid && playbookUuid && eventPlaybookUuid !== playbookUuid) {
    return { kind: 'ignore' };
  }

  const name = String(
    eventName || data.event_name || data.eventType || data.event_type || '',
  );

  // Another feature's traffic on the shared hub. Without this the panel drove
  // itself off matter-analysis and research events, which is where the
  // "welcome"/"preparing…" lines came from.
  if (!eventPlaybookUuid) {
    const lowered = name.toLowerCase();
    if (FOREIGN_EVENT_PREFIXES.some((prefix) => lowered.startsWith(prefix))) {
      return { kind: 'ignore' };
    }
    if (FOREIGN_ID_KEYS.some((key) => str(data[key]))) {
      return { kind: 'ignore' };
    }
  }

  const subType = String(data.sub_type || data.subType || '').toLowerCase();
  const status = String(data.status || '').toLowerCase();
  const message = playbookStr(data.message);
  const narration =
    playbookStr(data.narration) ||
    playbookStr(data.narration_text) ||
    playbookStr(data.narrationText);

  // Hub-level connection noise carries no playbook meaning.
  if (
    !message &&
    (subType === 'connected' || subType === 'disconnected' || subType === 'system')
  ) {
    return { kind: 'ignore' };
  }

  // The pipeline's generic opener. It says nothing about this playbook and its
  // copy is written for matter creation, so it never reaches the screen.
  const rawStepName = str(data.step_name) || str(data.stepName) || str(data.step);
  if (subType === 'welcome' || (rawStepName && stepKeyOf(rawStepName) === 'welcome')) {
    return { kind: 'ignore' };
  }

  const isFailure =
    status === 'failed' ||
    subType === 'failed' ||
    subType === 'error' ||
    (!!data.error && subType !== 'step_failed');

  if (isFailure) {
    return {
      kind: 'failed',
      playbookUuid: eventPlaybookUuid,
      error: playbookStr(data.error) || message || 'Extraction failed',
      failedDocuments: normalizeFailedDocuments(data.failed_documents),
    };
  }

  const isCompletion =
    status === 'completed' ||
    subType === 'completed' ||
    subType === 'complete' ||
    name === 'ai_processing_complete';

  if (isCompletion) {
    return {
      kind: 'completed',
      playbookUuid: eventPlaybookUuid,
      message,
      failedDocuments: normalizeFailedDocuments(data.failed_documents),
    };
  }

  const stepName = rawStepName && !isForeignCopy(rawStepName)
    ? playbookStepLabel(rawStepName)
    : null;
  const stepIndex =
    typeof data.step_index === 'number'
      ? data.step_index
      : typeof data.stepIndex === 'number'
        ? data.stepIndex
        : null;
  const stepTotal =
    typeof data.step_total === 'number'
      ? data.step_total
      : typeof data.total_steps === 'number'
        ? data.total_steps
        : null;

  const files = normalizeFileRows(data);

  // Progress with nothing to say about a step, a file, or a percentage moves
  // no pixel — drop it rather than re-rendering the panel.
  const percent = clampPercent(data.progress_percent ?? data.percent);
  if (!stepName && !files.length && percent === null && !message && !narration) {
    return { kind: 'ignore' };
  }

  const stepStatus: ExtractionStepStatus =
    subType === 'step_completed'
      ? 'completed'
      : subType === 'step_failed'
        ? 'failed'
        : 'in_progress';

  return {
    kind: 'progress',
    playbookUuid: eventPlaybookUuid,
    // Keyed on the RAW step id: the label is presentation, and keying on it
    // would open a second row the day the engine relabels a step mid-run.
    stepId: (rawStepName && stepKeyOf(rawStepName)) || (stepIndex !== null ? `step_${stepIndex}` : null),
    stepName,
    stepStatus,
    stepIndex,
    stepTotal,
    percent,
    message,
    narration,
    files,
  };
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

const mergeFiles = (
  existing: ExtractionFileProgress[],
  incoming: ExtractionFileProgress[],
): ExtractionFileProgress[] => {
  if (!incoming.length) return existing;
  const merged = [...existing];
  incoming.forEach((row) => {
    const index = merged.findIndex((f) => f.name === row.name);
    if (index === -1) {
      merged.push(row);
      return;
    }
    merged[index] = {
      ...merged[index],
      ...row,
      // A file already at 100 must not slide backwards on a late generic event.
      percent: row.percent ?? merged[index].percent,
      message: row.message ?? merged[index].message,
    };
  });
  return merged;
};

/**
 * Derived percentage, used only when the engine doesn't send one: completed
 * steps out of the total it announced (or out of what we've seen, which makes
 * the bar honest rather than pinned at 100 on the first step).
 */
const derivePercent = (steps: ExtractionRunStep[], stepTotal: number): number => {
  const total = Math.max(stepTotal, steps.length);
  if (!total) return 0;
  const done = steps.filter((s) => s.status === 'completed').length;
  const active = steps.some((s) => s.status === 'in_progress') ? 0.5 : 0;
  return Math.min(100, Math.round(((done + active) / total) * 100));
};

/**
 * The bar's next value.
 *
 * The engine's number wins when it is ahead, but it cannot pull the bar BELOW
 * what the steps themselves prove or below where it already stood: a pipeline
 * that reports `progress_percent: 0` on every frame — which is exactly what
 * left the header reading "0%" beside five completed steps — no longer flattens
 * a run that is visibly most of the way done.
 */
const nextPercent = (
  current: number,
  reported: number | null,
  steps: ExtractionRunStep[],
  stepTotal: number,
): number => Math.min(100, Math.max(current, reported ?? 0, derivePercent(steps, stepTotal)));

export function reduceExtractionRun(
  state: ExtractionRun,
  event: PlaybookExtractionEvent,
): ExtractionRun {
  switch (event.kind) {
    case 'ignore':
      return state;

    case 'completed': {
      // Everything still open reads as done: the engine only sends this once
      // the rules are written, and a step left spinning would say otherwise.
      const steps = state.steps.map((s) =>
        s.status === 'failed' ? s : { ...s, status: 'completed' as const },
      );
      const failedNames = new Set(event.failedDocuments.map((d) => d.name));
      return {
        ...state,
        status: 'completed',
        steps,
        percent: 100,
        files: state.files.map((f) =>
          failedNames.has(f.name)
            ? { ...f, status: 'failed' as const }
            : { ...f, status: 'completed' as const, percent: 100 },
        ),
        failedDocuments: event.failedDocuments,
        message: event.message ?? state.message,
        // The run is over: a mid-flight aside about what it was doing is stale.
        narration: null,
        stepCurrent: Math.max(state.stepTotal, steps.length),
        error: null,
      };
    }

    case 'failed':
      return {
        ...state,
        status: 'failed',
        steps: state.steps.map((s) =>
          s.status === 'in_progress' ? { ...s, status: 'failed' as const } : s,
        ),
        failedDocuments: event.failedDocuments.length
          ? event.failedDocuments
          : state.failedDocuments,
        narration: null,
        error: event.error,
      };

    case 'progress': {
      const narration = event.narration ?? null;
      let steps = state.steps;
      if (event.stepId) {
        const index = steps.findIndex((s) => s.id === event.stepId);
        if (index === -1) {
          // A new step arriving means the previous one finished — the engine
          // does not always send an explicit `step_completed` for it.
          steps = [
            ...steps.map((s) =>
              s.status === 'in_progress' ? { ...s, status: 'completed' as const } : s,
            ),
            {
              id: event.stepId,
              name: event.stepName || playbookStepLabel(event.stepId),
              status: event.stepStatus,
              message: event.message || undefined,
              narration: narration || undefined,
            },
          ];
        } else {
          steps = steps.map((s, i) =>
            i === index
              ? {
                  ...s,
                  name: event.stepName || s.name,
                  status: event.stepStatus,
                  message: event.message || s.message,
                  narration: narration || s.narration,
                }
              : s,
          );
        }
      }

      // The engine's index is 0-based, but one that counts from 1 would
      // otherwise report "Step 6 of 5" on its last step — and inflate the
      // total to 6 to make room for it.
      const reportedTotal = event.stepTotal ?? 0;
      const reportedCurrent =
        event.stepIndex === null
          ? null
          : reportedTotal > 0 && event.stepIndex >= reportedTotal
            ? event.stepIndex
            : event.stepIndex + 1;

      const stepTotal = Math.max(
        reportedTotal,
        state.stepTotal,
        steps.length,
        reportedCurrent ?? 0,
      );

      return {
        ...state,
        status: 'running',
        steps,
        stepTotal,
        stepCurrent: Math.max(
          reportedCurrent ?? steps.filter((s) => s.status !== 'pending').length,
          0,
        ),
        percent: nextPercent(state.percent, event.percent, steps, stepTotal),
        files: mergeFiles(state.files, event.files),
        message: event.message ?? state.message,
        narration: narration ?? state.narration,
      };
    }

    default:
      return state;
  }
}
